import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { ShieldCheck, Zap, ChevronRight, CreditCard, Check, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { waLink, formatPrice, BNPL } from "@/lib/constants";
import { Skeleton } from "@/components/ui/skeleton";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActiveImg(0);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        api
          .get("/products", { params: { brand: r.data.brand } })
          .then((rr) => setRelated(rr.data.filter((p) => p.slug !== slug).slice(0, 4)));
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-32 text-center sm:px-6">
        <Seo title="Produk tidak ditemukan" />
        <h1 className="font-heading text-3xl font-700 text-white">Produk tidak ditemukan</h1>
        <Link to="/katalog" className="mt-6 inline-flex items-center gap-2 rounded-full bg-berry px-6 py-3 font-semibold text-white">
          <ArrowLeft className="h-4 w-4" /> Kembali ke Katalog
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-2">
          <Skeleton className="h-[420px] rounded-3xl bg-white/5" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-2/3 bg-white/5" />
            <Skeleton className="h-6 w-1/3 bg-white/5" />
            <Skeleton className="h-24 w-full bg-white/5" />
          </div>
        </div>
      </div>
    );
  }

  const price = formatPrice(product.price);
  const gallery = product.images && product.images.length ? product.images : [product.image];
  const waText = `${product.name}${product.variant ? " " + product.variant : ""}`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <Seo
        title={`${product.name} ${product.variant || ""}`.trim()}
        description={`${product.name} ${product.variant || ""} — Brand New & Garansi Resmi di BigBerry Store. Bisa cicilan. Beli via WhatsApp.`}
        path={`/produk/${product.slug}`}
      />

      {/* JSON-LD Product */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: `${product.name} ${product.variant || ""}`.trim(),
            brand: { "@type": "Brand", name: product.brand },
            image: product.image,
            category: product.category,
            description: product.description || `${product.name} Brand New & Garansi Resmi`,
          }),
        }}
      />

      <nav className="mb-6 flex items-center gap-1 text-sm text-gray-500">
        <Link to="/" className="hover:text-white">Beranda</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/brand/${encodeURIComponent(product.brand)}`} className="hover:text-white">{product.brand}</Link>
        <ChevronRight className="h-4 w-4" />
        <span className="truncate text-gray-300">{product.name}</span>
      </nav>

      <div className="grid gap-10 lg:grid-cols-2">
        {/* Gallery */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="overflow-hidden rounded-3xl border border-white/5 bg-[#0B0C10] p-8">
            <img
              src={gallery[activeImg] || "/assets/logo.png"}
              alt={`${product.name} ${product.variant || ""} - BigBerry Store`}
              className="mx-auto h-80 w-full object-contain md:h-[420px]"
            />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 flex gap-3">
              {gallery.map((img, i) => (
                <button
                  key={`${img}-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`h-20 w-20 overflow-hidden rounded-xl border bg-[#0B0C10] p-2 ${
                    i === activeImg ? "border-berry" : "border-white/10"
                  }`}
                >
                  <img src={img} alt="" className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Info */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
          <span className="text-sm font-medium uppercase tracking-widest text-berry">{product.brand}</span>
          <h1 className="mt-2 font-heading text-3xl font-800 tracking-tight text-white md:text-4xl">{product.name}</h1>
          {product.variant && <p className="mt-2 text-gray-400">Varian: {product.variant}</p>}

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-white">
              <Zap className="h-3.5 w-3.5" /> Brand New
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-electric/20 px-3 py-1 text-xs font-medium uppercase tracking-wider text-blue-400">
              <ShieldCheck className="h-3.5 w-3.5" /> Garansi Resmi
            </span>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wider ${product.in_stock ? "bg-whatsapp/20 text-whatsapp" : "bg-white/10 text-gray-400"}`}>
              {product.in_stock ? "Tersedia" : "Stok Habis"}
            </span>
          </div>

          <div className="mt-6 rounded-2xl border border-white/5 bg-[#12141C] p-6">
            {price ? (
              <p data-testid="detail-price" className="font-heading text-3xl font-800 text-white">{price}</p>
            ) : (
              <div>
                <p className="text-sm text-gray-400">Harga</p>
                <p data-testid="detail-price" className="font-heading text-2xl font-700 text-white">Hubungi via WhatsApp</p>
              </div>
            )}
            <a
              href={waLink(waText)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="detail-whatsapp"
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-whatsapp px-6 py-4 text-base font-bold text-white transition-all duration-300 hover:scale-[1.02] hover:bg-whatsapp-dark"
            >
              <FaWhatsapp className="h-5 w-5" /> Beli via WhatsApp
            </a>
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-electric/30 bg-electric/10 px-4 py-3 text-sm text-blue-200">
              <CreditCard className="h-4 w-4 shrink-0" />
              <span>Bisa dicicil: {BNPL.slice(0, 4).join(", ")} & kartu kredit</span>
            </div>
          </div>

          {product.description && (
            <div className="mt-6">
              <h3 className="font-heading text-lg font-600 text-white">Deskripsi</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-400">{product.description}</p>
            </div>
          )}

          {product.specs && product.specs.length > 0 && (
            <div className="mt-6">
              <h3 className="font-heading text-lg font-600 text-white">Spesifikasi</h3>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {product.specs.map((s, i) => (
                  <li key={`${s}-${i}`} className="flex items-start gap-2 text-sm text-gray-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-berry" /> {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>
      </div>

      {related.length > 0 && (
        <div className="mt-20">
          <h2 className="mb-8 font-heading text-2xl font-700 tracking-tight text-white">Produk {product.brand} Lainnya</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
